import { useState } from 'react';
import { api } from '../../../api/client.js';
import { ConfirmDialog } from '../../../components/ConfirmDialog.jsx';

// Item options (spec §16: e.g. "Size" single-select, "Add-ons" multi-
// select), each with values that can carry a price difference.
export function OptionsEditor({ menuId, categoryId, itemId, options, onChanged }) {
  const base = `/api/menus/${menuId}/categories/${categoryId}/items/${itemId}/options`;
  const [showAddOption, setShowAddOption] = useState(false);

  return (
    <div className="extras-editor extras-editor--choices">
      <div className="extras-editor__heading">
        <div>
          <h4>Customer choices</h4>
          <p>Sizes, add-ons, flavours, or any choice a guest can make. For portions, set the dish price to the smallest size and enter each larger size's price difference.</p>
        </div>
      </div>
      {options.map((option) => (
        <OptionGroupEditor key={option.id} base={base} option={option} onChanged={onChanged} />
      ))}
      {showAddOption ? (
        <AddOptionForm
          base={base}
          onDone={() => {
            setShowAddOption(false);
            onChanged();
          }}
          onCancel={() => setShowAddOption(false)}
        />
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => setShowAddOption(true)}>
          + Add a choice group
        </button>
      )}
    </div>
  );
}

function AddOptionForm({ base, onDone, onCancel }) {
  const [name, setName] = useState('');
  const [selectionType, setSelectionType] = useState('SINGLE');
  const [isRequired, setIsRequired] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(base, { name, selectionType, isRequired });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="choice-group-form">
      {error && <p className="field-error">{error}</p>}
      <div className="field">
        <label htmlFor="option-name">Choice name</label>
        <input id="option-name" type="text" required placeholder="e.g. Size" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="option-selection">Selection</label>
        <select id="option-selection" value={selectionType} onChange={(e) => setSelectionType(e.target.value)}>
          <option value="SINGLE">Choose one</option>
          <option value="MULTI">Choose multiple</option>
        </select>
      </div>
      <label className="checkbox" style={{ marginBottom: 10 }}>
        <input type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
        Required
      </label>
      <div className="choice-group-form__actions">
        <button type="submit" className="btn btn-sm btn-primary" disabled={submitting}>
          Add choice group
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function OptionGroupEditor({ base, option, onChanged }) {
  const optionBase = `${base}/${option.id}`;
  const [addingValue, setAddingValue] = useState(false);
  const [label, setLabel] = useState('');
  const [priceDelta, setPriceDelta] = useState('');
  const [editingValueId, setEditingValueId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [editingPriceDelta, setEditingPriceDelta] = useState('');
  const [error, setError] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [savingValue, setSavingValue] = useState(false);

  function beginEditingValue(value) {
    setError(null);
    setEditingValueId(value.id);
    setEditingLabel(value.label);
    setEditingPriceDelta(String(value.priceDelta ?? 0));
  }

  function cancelEditingValue() {
    setEditingValueId(null);
    setEditingLabel('');
    setEditingPriceDelta('');
  }

  async function handleDeleteOption() {
    setDeleting(true);
    setError(null);
    try {
      if (confirmTarget.type === 'group') await api.delete(optionBase);
      else await api.delete(`${optionBase}/values/${confirmTarget.value.id}`);
      setConfirmTarget(null);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleAddValue(event) {
    event.preventDefault();
    setError(null);
    setAdding(true);
    try {
      await api.post(`${optionBase}/values`, { label, priceDelta: priceDelta ? Number(priceDelta) : 0 });
      setLabel('');
      setPriceDelta('');
      setAddingValue(false);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function handleUpdateValue(event) {
    event.preventDefault();
    setError(null);
    setSavingValue(true);
    try {
      await api.patch(`${optionBase}/values/${editingValueId}`, {
        label: editingLabel,
        priceDelta: Number(editingPriceDelta),
      });
      cancelEditingValue();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingValue(false);
    }
  }

  return (
    <div className="choice-group">
      <div className="choice-group__heading">
        <div>
          <strong>{option.name}</strong>{' '}
          <span className="field-hint">
            {option.selectionType === 'MULTI' ? 'Choose multiple' : 'Choose one'}
            {option.isRequired ? ' · Required' : ' · Optional'}
          </span>
        </div>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setError(null); setConfirmTarget({ type: 'group' }); }}>
          Delete group
        </button>
      </div>
      {error && <p className="field-error">{error}</p>}
      {option.values.length > 0 && (
        <div className="choice-values">
          {option.values.map((value) => (editingValueId === value.id ? (
            <form key={value.id} onSubmit={handleUpdateValue} className="choice-value-form">
              <input
                type="text"
                required
                value={editingLabel}
                onChange={(e) => setEditingLabel(e.target.value)}
              />
              <input
                type="number"
                step="0.01"
                required
                value={editingPriceDelta}
                onChange={(e) => setEditingPriceDelta(e.target.value)}
              />
              <button type="submit" className="btn btn-sm btn-primary" disabled={savingValue}>
                {savingValue ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={cancelEditingValue}>
                Cancel
              </button>
            </form>
          ) : (
            <div key={value.id} className="choice-value">
              <span>
                {value.label}
                {value.priceDelta ? ` — ${value.priceDelta > 0 ? '+' : ''}${value.priceDelta}` : ''}
              </span>
              <div className="choice-value__actions">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => beginEditingValue(value)}>
                  Edit
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setError(null); setConfirmTarget({ type: 'value', value }); }}>
                  Remove
                </button>
              </div>
            </div>
          )))}
        </div>
      )}
      {addingValue ? (
        <form onSubmit={handleAddValue} className="choice-value-form">
          <input
            type="text"
            placeholder="e.g. Small"
            required
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <input
            type="number"
            step="0.01"
            placeholder="+price"
            value={priceDelta}
            onChange={(e) => setPriceDelta(e.target.value)}
          />
          <button type="submit" className="btn btn-sm" disabled={adding}>
            {adding ? 'Adding…' : 'Add'}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAddingValue(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => setAddingValue(true)}>
          + Add a choice
        </button>
      )}
      {confirmTarget && <ConfirmDialog
        title={confirmTarget.type === 'group' ? `Delete “${option.name}”?` : `Remove “${confirmTarget.value.label}”?`}
        description={confirmTarget.type === 'group' ? 'This removes the choice group and all its values from the menu item.' : 'This choice will no longer be available to customers.'}
        confirmLabel={confirmTarget.type === 'group' ? 'Delete group' : 'Remove choice'}
        busy={deleting}
        error={error}
        onConfirm={handleDeleteOption}
        onCancel={() => setConfirmTarget(null)}
      />}
    </div>
  );
}
